import { cva, type VariantProps } from 'class-variance-authority';
import type { ComponentProps } from 'react';

import type { VsImageProps } from '@vs-shop/headless/useVsImage';
import Image from '@vs-shop/image-link/Image';
import { Button } from '@vs-shop/ui/button';
import { clsxm, type ClassValue } from '@vs-shop/utils';

import { RenderedPart, type RenderElement } from '../../internal/renderedPart.tsx';

/**
 * The order line item: one product as a cart, a checkout summary or a placed
 * order shows it — its photograph, its name, how many of it, what it costs, and
 * the way out of the order.
 *
 * It comes in two container shapes, because the two are different ELEMENTS
 * rather than two looks:
 *
 *   <OrderLineItemTable>
 *     <OrderLineItemTableHead>
 *       <OrderLineItemTableHeadRow>
 *         <TableHeadGhost>Položka</TableHeadGhost>
 *       </OrderLineItemTableHeadRow>
 *     </OrderLineItemTableHead>
 *     <OrderLineItemTableBody>
 *       <OrderLineItemRow variant={OrderLineItemRowVariant.RULED}>
 *         <TableCellGhost>…</TableCellGhost>
 *       </OrderLineItemRow>
 *     </OrderLineItemTableBody>
 *   </OrderLineItemTable>
 *
 *   <OrderLineItemList>
 *     <OrderLineItem>…</OrderLineItem>
 *   </OrderLineItemList>
 *
 * A part that renders `<tr>` is invalid inside a `<ul>`, so the row and the
 * stacked item are two parts rather than one with a `variant` — the decision the
 * ticket for this rung asked to make explicitly, recorded with its reason in
 * `packages/rcprofi/.docs/MODULARIZED.md`. What the two DO share is everything
 * inside them: the media frame, the name, the availability sentence, the money
 * and the way out. Those parts carry `variant`, and there the difference really
 * is only how a box paints itself.
 *
 * `variant` is the appearance axis, required wherever there is one, with no
 * `defaultVariants` anywhere: an appearance decided by omission is invisible in
 * review. The opt-out is the named `unstyled` arm.
 *
 * Nothing here formats money, resolves an image URL, or knows what a cart is.
 * Copy arrives as children, the quantity control arrives as a composed node —
 * the tenant's own, which owns the mutation — and image URLs arrive final and
 * renderable (spec § "Media parts").
 *
 * Server-first: every part in this file is a plain component. The disclosure
 * that opens the cross-selling rail sits in `./orderLineItemCrossSell.tsx`,
 * over the client collapsible primitive.
 */

/* ── the desktop container ────────────────────────────────────────────── */

/**
 * The scroll frame around the table. Load-bearing and invisible when missing:
 * a line-item table is six columns wide and overruns a narrow page without it,
 * so it belongs to the part rather than to whoever composes one.
 */
const orderLineItemTableFrameClasses = 'relative w-full overflow-auto';

/** The table itself, filling whatever box the page gives the frame. */
const orderLineItemTableClasses = 'w-full';

export type OrderLineItemTableProps = ComponentProps<'table'>;

/**
 * The desktop line-item table, two elements deep: the scroll frame and the
 * table inside it. `className` lands on the FRAME, so a page sizing or hiding
 * the table styles the box that actually takes the space.
 */
export const OrderLineItemTable = ({ className, children, ...props }: OrderLineItemTableProps) => (
  <div
    data-slot="order-line-item-table-frame"
    className={clsxm(orderLineItemTableFrameClasses, className)}
  >
    <table data-slot="order-line-item-table" className={orderLineItemTableClasses} {...props}>
      {children}
    </table>
  </div>
);

export type OrderLineItemTableHeadProps = ComponentProps<'thead'>;

/** The table's head. No recipe: what it paints is the row's. */
export const OrderLineItemTableHead = (props: OrderLineItemTableHeadProps) => (
  <thead data-slot="order-line-item-table-head" {...props} />
);

/**
 * The head row. Carries the column headings' shared spacing and casing rather
 * than repeating them on each cell, so a page composing `TableHeadGhost` cells
 * gets the section's heading row without restating it six times.
 */
const orderLineItemTableHeadRowClasses = '[&>th]:pb-4 [&>th]:uppercase';

export type OrderLineItemTableHeadRowProps = ComponentProps<'tr'>;

/** The row of column headings. */
export const OrderLineItemTableHeadRow = ({
  className,
  ...props
}: OrderLineItemTableHeadRowProps) => (
  <tr
    data-slot="order-line-item-table-head-row"
    className={clsxm(orderLineItemTableHeadRowClasses, className)}
    {...props}
  />
);

/**
 * The table's body.
 *
 * Owns the rules that close the list off at both ends — a top rule under the
 * headings and no trailing one — because those are facts about the LIST rather
 * than about any row in it, and a row cannot see whether it is first or last.
 */
const orderLineItemTableBodyClasses = '[&_tr:first-child]:border-t [&_tr:last-child]:border-0';

export type OrderLineItemTableBodyProps = ComponentProps<'tbody'>;

/** The rows' container. */
export const OrderLineItemTableBody = ({ className, ...props }: OrderLineItemTableBodyProps) => (
  <tbody
    data-slot="order-line-item-table-body"
    className={clsxm(orderLineItemTableBodyClasses, className)}
    {...props}
  />
);

/**
 * One line item as a table row.
 *
 * The rule under it is an appearance the ROW decides per item: a row that runs
 * straight into a block of its own below it — an offer, a discount line — closes
 * with nothing, so the two read as one item rather than two.
 */
export const orderLineItemRowVariants = cva('', {
  variants: {
    variant: {
      ruled: 'border-foreground/20 border-b',
      /** Runs into whatever the item puts below it, so it closes with nothing. */
      open: '',
    },
  },
});

type OrderLineItemRowVariantName = NonNullable<
  VariantProps<typeof orderLineItemRowVariants>['variant']
>;

export type OrderLineItemRowVariant = OrderLineItemRowVariantName;

/** `satisfies` binds these to the recipe above, so the two cannot drift. */
export const OrderLineItemRowVariant = {
  RULED: 'ruled',
  OPEN: 'open',
} as const satisfies Record<string, OrderLineItemRowVariantName>;

/** Required and closed — not `VariantProps`, which makes it optional. */
export type OrderLineItemRowVariantProps = { readonly variant: OrderLineItemRowVariant };

export type OrderLineItemRowProps = ComponentProps<'tr'> & OrderLineItemRowVariantProps;

/** One line item, as a row of cells. */
export const OrderLineItemRow = ({ className, variant, ...props }: OrderLineItemRowProps) => (
  <tr
    data-slot="order-line-item-row"
    className={clsxm(orderLineItemRowVariants({ variant }), className)}
    {...props}
  />
);

/* ── the stacked container ────────────────────────────────────────────── */

/**
 * The stacked line items, ruled apart.
 *
 * `divide-y` rather than a rule per item: the separator belongs between two
 * items, and an item cannot see whether it has a neighbour.
 */
export const orderLineItemListVariants = cva('', {
  variants: {
    variant: {
      /** A page's own list: ruled between items. */
      stacked: 'divide-foreground/20 flex flex-col divide-y',
      /**
       * A panel's list: nothing at all. Each item is a card that separates
       * itself, so a rule between two of them would run across the gap.
       */
      drawer: '',
      /** A summary card's list: ruled between items, quieter than a page's. */
      summary: 'divide-foreground/10 flex flex-col divide-y',
      /** The opt-out: a tenant painting the list itself. */
      unstyled: '',
    },
  },
});

export type OrderLineItemListProps = ComponentProps<'ul'> & OrderLineItemContainerVariantProps;

/** The stacked line items. */
export const OrderLineItemList = ({ className, variant, ...props }: OrderLineItemListProps) => (
  <ul
    data-slot="order-line-item-list"
    className={clsxm(orderLineItemListVariants({ variant }), className)}
    {...props}
  />
);

/**
 * One line item as a stacked card: the media and naming block, then the
 * quantity row, then whatever the item offers alongside itself.
 *
 * Closes the list at both ends with its own rules, which is what keeps the
 * stack bounded where the `divide-y` between items only rules the gaps.
 */
export const orderLineItemVariants = cva('', {
  variants: {
    variant: {
      /** Ruled off from its neighbours, as a page's own list of items. */
      stacked: 'border-foreground/20 flex flex-col gap-y-3 pt-6 pb-6 first:border-t last:border-b',
      /**
       * A card of its own inside a panel: ringed rather than ruled, and laid
       * along the panel's width from the tablet step up, where the panel is wide
       * enough to put the quantity row beside the photograph.
       */
      drawer:
        'tab:flex-row bg-background ring-foreground/10 mb-2 flex flex-col gap-y-7 rounded-xl px-5 py-4 ring ring-inset last:mb-0',
      /**
       * A line on a summary card: no rule of its own — the list rules between
       * items — and a first line that opens the card with a rule above it from
       * the laptop step, where the card also carries the title that rule sits
       * under.
       */
      summary:
        'first:lap:mt-5 first:lap:border-t first:lap:pt-5 flex w-full flex-col justify-between py-5 first:pt-0',
      /** The opt-out: a tenant painting the item itself. */
      unstyled: '',
    },
  },
});

type OrderLineItemContainerVariantName = NonNullable<
  VariantProps<typeof orderLineItemVariants>['variant']
>;

export type OrderLineItemContainerVariant = OrderLineItemContainerVariantName;

/**
 * Which container a stacked item stands in — its own axis, separate from the
 * section's because its arms are: there is no stacked item in a table, and the panel's card is a
 * surface the table knows nothing about. Bound to the recipe by `satisfies`.
 */
export const OrderLineItemContainerVariant = {
  /** A page's list of items. */
  STACKED: 'stacked',
  /** A card inside a panel. */
  DRAWER: 'drawer',
  /** A line on a summary card. */
  SUMMARY: 'summary',
  /** The opt-out: a tenant painting the item itself. */
  UNSTYLED: 'unstyled',
} as const satisfies Record<string, OrderLineItemContainerVariantName>;

/** Required and closed — not `VariantProps`, which makes it optional. */
export type OrderLineItemContainerVariantProps = {
  readonly variant: OrderLineItemContainerVariant;
};

export type OrderLineItemProps = ComponentProps<'li'> & OrderLineItemContainerVariantProps;

/** One line item, stacked. */
export const OrderLineItem = ({ className, variant, ...props }: OrderLineItemProps) => (
  <li
    data-slot="order-line-item"
    className={clsxm(orderLineItemVariants({ variant }), className)}
    {...props}
  />
);

/* ── what both containers hold ────────────────────────────────────────── */

/**
 * The media frame: the box the product's photograph sits in, and the
 * positioning context the age mark hangs in.
 *
 * `relative` is not decoration — {@link OrderLineItemAgeBadge} positions itself
 * against this element. The frame is usually also the link to the product, which
 * is what `render` is for: a whole-surface click expressed as the element the
 * tenant's router supplies, rather than an anchor wrapped around the anatomy.
 */
export const orderLineItemMediaVariants = cva(
  'relative flex shrink-0 items-center justify-center',
  {
    variants: {
      variant: {
        /** Beside five other columns, so the frame can afford its full size. */
        table: 'h-[90px] w-[120px]',
        /** Beside the name and the way out, on a phone's width. */
        stacked: 'h-[70px] w-[94px]',
        /** Inside a panel, where the frame grows with the panel rather than the page. */
        drawer: 'tab:h-[100px] tab:w-[133px] h-[70px] w-[85px]',
        /**
         * On a summary card, where the photograph is a reminder rather than the
         * subject: the smallest frame of the four, and the only one that paints
         * a surface of its own — the card's ground is the intense one, so a
         * contained shot needs a light box behind it to read.
         */
        summary: 'ring-foreground/10 bg-background h-[60px] w-20 rounded-lg ring-1',
        unstyled: '',
      },
    },
  }
);

type OrderLineItemVariantName = NonNullable<
  VariantProps<typeof orderLineItemMediaVariants>['variant']
>;

export type OrderLineItemVariant = OrderLineItemVariantName;

/**
 * The section's appearance axis, shared by every part whose look differs
 * between the containers a line item stands in. Derived from the media recipe
 * and bound to it by `satisfies`, so the union and the recipes cannot drift.
 */
export const OrderLineItemVariant = {
  /** A cell in the desktop table. */
  TABLE: 'table',
  /** A stacked card. */
  STACKED: 'stacked',
  /** A card inside a panel, at the panel's own widths. */
  DRAWER: 'drawer',
  /** A line on a summary card. */
  SUMMARY: 'summary',
  /** The opt-out: a tenant painting the part itself. */
  UNSTYLED: 'unstyled',
} as const satisfies Record<string, OrderLineItemVariantName>;

/** Required and closed — not `VariantProps`, which makes it optional. */
export type OrderLineItemVariantProps = { readonly variant: OrderLineItemVariant };

export type OrderLineItemMediaProps = ComponentProps<'div'> &
  OrderLineItemVariantProps & {
    /**
     * The element to paint instead of the default `<div>` — where a tenant hands
     * in its router's link, so the frame itself is the click target.
     */
    readonly render?: RenderElement;
  };

/** The product's photograph, framed. */
export const OrderLineItemMedia = ({
  className,
  variant,
  render,
  ...props
}: OrderLineItemMediaProps) => (
  <RenderedPart
    dataSlot="order-line-item-media"
    className={clsxm(orderLineItemMediaVariants({ variant }), className)}
    render={render}
    {...props}
  />
);

/**
 * The photograph itself, contained rather than cropped: a product shot is a
 * subject on a background, and filling the frame would cut the subject.
 *
 * `src` is final and renderable — CDN/Liip resolution stays app-side — and there
 * is no `sources`, because a line item framing one crop is not art-directed.
 */
const orderLineItemImageClasses = 'h-full w-full object-contain';

export type OrderLineItemImageProps = Omit<VsImageProps, 'className' | 'fill'> & {
  readonly className?: ClassValue;
};

/**
 * The product's photograph.
 *
 * No `variant`: the photograph fills the frame in both containers — the frame is
 * what changes size, not what it holds.
 */
export const OrderLineItemImage = ({ className, ...props }: OrderLineItemImageProps) => (
  <Image
    data-slot="order-line-item-image"
    className={clsxm(orderLineItemImageClasses, className)}
    {...props}
  />
);

/**
 * The age-restriction mark, at the trailing bottom corner of the media frame.
 *
 * Carries the surface's own background rather than relying on the photograph
 * behind it, so it stays legible over any imagery. What goes inside is the
 * tenant's mark; this ships the box.
 */
const orderLineItemAgeBadgeClasses = 'bg-background absolute right-0 bottom-0 rounded-full';

export type OrderLineItemAgeBadgeProps = ComponentProps<'div'>;

/** The age-restriction mark's box. */
export const OrderLineItemAgeBadge = ({ className, ...props }: OrderLineItemAgeBadgeProps) => (
  <div
    data-slot="order-line-item-age-badge"
    className={clsxm(orderLineItemAgeBadgeClasses, className)}
    {...props}
  />
);

/**
 * The media frame beside the naming block — the head of a line item in both
 * containers.
 */
export const orderLineItemLeadVariants = cva('flex', {
  variants: {
    variant: {
      table: 'items-center gap-4',
      stacked: 'items-center gap-4',
      /**
       * Runs the panel's width and hangs its contents from the top: the naming
       * block beside it is a column of its own, and centring it against the
       * photograph would drop the name to the middle of the frame.
       */
      drawer: 'w-full gap-x-5',
      /**
       * Runs the card's width and pushes the photograph away from the copy: a
       * summary line has no third element, so the gap is what separates them.
       */
      summary: 'w-full justify-between gap-x-6',
      unstyled: '',
    },
  },
});

export type OrderLineItemLeadProps = ComponentProps<'div'> & OrderLineItemVariantProps;

/** The photograph beside what names the product. */
export const OrderLineItemLead = ({ className, variant, ...props }: OrderLineItemLeadProps) => (
  <div
    data-slot="order-line-item-lead"
    className={clsxm(orderLineItemLeadVariants({ variant }), className)}
    {...props}
  />
);

/**
 * The product's name.
 *
 * A neutral element, never a heading: a line item is furniture a page repeats,
 * and a heading inside one competes with the page's own outline. A page that
 * wants this text as a heading composes `@vs-shop/ui/heading` through `render`,
 * where the level is a visible decision — and a page that wants it to navigate
 * hands in its router's link the same way.
 */
export const orderLineItemNameVariants = cva('font-medium tracking-tight', {
  variants: {
    variant: {
      table: 'text-foreground text-lg/5',
      /** Takes the width left over beside the way out, so the two share the row. */
      stacked: 'flex flex-1 text-base/5',
      /** Set over the quantity row rather than beside anything, so it runs the panel's width. */
      drawer: 'text-foreground tab:text-lg/6 text-left text-base/5',
      /** On a summary card, at the table's own size: it is the line's loudest string. */
      summary: 'text-foreground text-lg/5',
      unstyled: '',
    },
  },
});

export type OrderLineItemNameProps = ComponentProps<'div'> &
  OrderLineItemVariantProps & {
    /** The element to paint instead of the default `<div>`. */
    readonly render?: RenderElement;
  };

/** The product's name. */
export const OrderLineItemName = ({
  className,
  variant,
  render,
  ...props
}: OrderLineItemNameProps) => (
  <RenderedPart
    dataSlot="order-line-item-name"
    className={clsxm(orderLineItemNameVariants({ variant }), className)}
    render={render}
    {...props}
  />
);

/**
 * The naming block of a stacked item: the name row over the availability
 * sentence, taking the width left beside the photograph.
 */
export const orderLineItemCopyVariants = cva('flex flex-col', {
  variants: {
    variant: {
      table: 'flex-1 gap-y-2',
      stacked: 'flex-1 gap-y-2',
      /** Takes the panel's width and holds the quantity row it also carries. */
      drawer: 'w-full gap-y-5',
      /** Takes the card's width, and sets the measure every line under it reads at. */
      summary: 'w-full text-base/6',
      unstyled: '',
    },
  },
});

export type OrderLineItemCopyProps = ComponentProps<'div'> & OrderLineItemVariantProps;

/** What names the product, stacked over what says whether it can be had. */
export const OrderLineItemCopy = ({ className, variant, ...props }: OrderLineItemCopyProps) => (
  <div
    data-slot="order-line-item-copy"
    className={clsxm(orderLineItemCopyVariants({ variant }), className)}
    {...props}
  />
);

/**
 * The name at one end of a row, whatever the line puts opposite it at the other
 * — the way out of the order in a cart, what the line costs on a summary card.
 *
 * Its own part rather than the naming block's first element, because that is
 * where a tenant reaches to put something else beside the name.
 */
export const orderLineItemNameRowVariants = cva('flex', {
  variants: {
    variant: {
      /** One row, filling the width the photograph leaves it. */
      flush: 'flex-1 justify-between gap-4',
      /**
       * Two lines on a phone and one from the tablet step: a name and a figure
       * both want the full measure where the line is a sidebar's width.
       */
      wrapped: 'tab:flex-row tab:gap-4 flex-col justify-between gap-2',
      /**
       * The same row set a weight up: the line naming a product a shopper
       * bought, against the lines naming what carried it and what came off it.
       */
      wrappedStrong: 'tab:flex-row tab:gap-4 flex-col justify-between gap-2 font-medium',
      /** The opt-out: a tenant laying the row out itself. */
      unstyled: '',
    },
  },
});

type OrderLineItemNameRowVariantName = NonNullable<
  VariantProps<typeof orderLineItemNameRowVariants>['variant']
>;

export type OrderLineItemNameRowVariant = OrderLineItemNameRowVariantName;

/**
 * Its own axis rather than the section's: the row has two looks and the
 * section's union has four containers, so borrowing it would declare arms no
 * caller can reach. `satisfies` binds these to the recipe above.
 */
export const OrderLineItemNameRowVariant = {
  /** One row, the name filling it. */
  FLUSH: 'flush',
  /** Two lines below the tablet step. */
  WRAPPED: 'wrapped',
  /** The same, set a weight up. */
  WRAPPED_STRONG: 'wrappedStrong',
  /** The opt-out: a tenant laying the row out itself. */
  UNSTYLED: 'unstyled',
} as const satisfies Record<string, OrderLineItemNameRowVariantName>;

/** Required and closed — not `VariantProps`, which makes it optional. */
export type OrderLineItemNameRowVariantProps = { readonly variant: OrderLineItemNameRowVariant };

export type OrderLineItemNameRowProps = ComponentProps<'div'> & OrderLineItemNameRowVariantProps;

/** The name beside whatever the line puts opposite it. */
export const OrderLineItemNameRow = ({
  className,
  variant,
  ...props
}: OrderLineItemNameRowProps) => (
  <div
    data-slot="order-line-item-name-row"
    className={clsxm(orderLineItemNameRowVariants({ variant }), className)}
    {...props}
  />
);

/**
 * How many of the product the line carries.
 *
 * One part, composed wherever the line has room for it: a summary card states
 * it beside the price on a phone and under the name from the tablet step, which
 * is two places no single element can occupy. Which of them a width shows is
 * the composition's, the way the drawer's quantity row already is.
 */
const orderLineItemCountClasses = 'text-foreground-muted';

export type OrderLineItemCountProps = ComponentProps<'div'>;

/** How many of it. */
export const OrderLineItemCount = ({ className, ...props }: OrderLineItemCountProps) => (
  <div
    data-slot="order-line-item-count"
    className={clsxm(orderLineItemCountClasses, className)}
    {...props}
  />
);

/**
 * Whether the product can be had, and how much of it.
 *
 * Four appearances rather than two, because the alert is genuinely one look in
 * both containers: a line nothing is left of paints the same in a table cell and
 * on a stacked card, so stating it once is the truthful list rather than a
 * duplicated pair. What the sentence SAYS is the tenant's — this paints it.
 */
export const orderLineItemStockVariants = cva('', {
  variants: {
    variant: {
      table: 'text-success des:text-base text-sm',
      stacked: 'text-success text-base/6 font-medium',
      /** Nothing left, in either container. */
      alert: 'text-destructive',
      unstyled: '',
    },
  },
});

type OrderLineItemStockVariantName = NonNullable<
  VariantProps<typeof orderLineItemStockVariants>['variant']
>;

export type OrderLineItemStockVariant = OrderLineItemStockVariantName;

/** `satisfies` binds these to the recipe above, so the two cannot drift. */
export const OrderLineItemStockVariant = {
  TABLE: 'table',
  STACKED: 'stacked',
  ALERT: 'alert',
  /** The opt-out: a tenant painting the sentence itself. */
  UNSTYLED: 'unstyled',
} as const satisfies Record<string, OrderLineItemStockVariantName>;

/** Required and closed — not `VariantProps`, which makes it optional. */
export type OrderLineItemStockVariantProps = { readonly variant: OrderLineItemStockVariant };

export type OrderLineItemStockProps = ComponentProps<'span'> & OrderLineItemStockVariantProps;

/** The availability sentence. */
export const OrderLineItemStock = ({ className, variant, ...props }: OrderLineItemStockProps) => (
  <span
    data-slot="order-line-item-stock"
    className={clsxm(orderLineItemStockVariants({ variant }), className)}
    {...props}
  />
);

/**
 * What the line costs.
 *
 * A span in both containers, so the same part can be a table cell's contents and
 * the trailing half of a stacked item's quantity row. The table appearance is a
 * baseline-aligned box, because a price and its currency sit on one line there;
 * the stacked one is a single figure.
 */
export const orderLineItemPriceVariants = cva('', {
  variants: {
    variant: {
      table: 'des:text-2xl/9 flex items-baseline gap-2 text-xl/9 tracking-tighter',
      stacked: 'text-lg/6 font-semibold',
      /** Grows with the panel: the figure is the panel's loudest line at its widest. */
      drawer: 'tab:text-xl/7 text-lg/7 font-medium',
      /**
       * On a summary card, at the measure the copy block sets: the card's loud
       * figure is its total, and a line that competed with it would flatten the
       * difference between what one item costs and what the order does.
       */
      summary: 'text-base font-medium',
      unstyled: '',
    },
  },
});

export type OrderLineItemPriceProps = ComponentProps<'span'> & OrderLineItemVariantProps;

/** A money figure on the line. */
export const OrderLineItemPrice = ({ className, variant, ...props }: OrderLineItemPriceProps) => (
  <span
    data-slot="order-line-item-price"
    className={clsxm(orderLineItemPriceVariants({ variant }), className)}
    {...props}
  />
);

/**
 * How many at one end and how much at the other: the quantity control beside the
 * price in a cart, the count beside it on a summary card.
 */
const orderLineItemQuantityRowClasses = 'flex items-center justify-between';

export type OrderLineItemQuantityRowProps = ComponentProps<'div'>;

/** How many, and how much. */
export const OrderLineItemQuantityRow = ({
  className,
  ...props
}: OrderLineItemQuantityRowProps) => (
  <div
    data-slot="order-line-item-quantity-row"
    className={clsxm(orderLineItemQuantityRowClasses, className)}
    {...props}
  />
);

/* ── the way out of the order ─────────────────────────────────────────── */

/**
 * The box the way-out control sits in, holding it at the trailing end of
 * whatever it is placed in.
 *
 * Not exported: it is anatomy below the line a tenant writes. `className` on the
 * part lands on the CONTROL, so nothing outside this file can reach this box —
 * an exported recipe for it would be a recipe with no caller.
 */
const orderLineItemRemoveFrameClasses = 'flex justify-end';

/**
 * The control that takes the line out of the order.
 *
 * A round target that grows with the viewport: a thumb needs more of it on a
 * phone than a pointer does on a desktop. Composed over `@vs-shop/ui`'s button
 * rather than painted from scratch, so its focus ring and its disabled state are
 * the platform's.
 */
const orderLineItemRemoveClasses = [
  'flex shrink-0 items-center justify-center rounded-full',
  'lap:size-10 des:size-12 size-6',
];

export type OrderLineItemRemoveProps = ComponentProps<'button'>;

/**
 * The way out of the order, two elements deep: the aligning box and the control
 * inside it. `className` lands on the CONTROL, and every native prop — the
 * handler, `disabled`, the label a tenant's own copy supplies — rides the spread
 * onto it as well.
 */
export const OrderLineItemRemove = ({ className, ...props }: OrderLineItemRemoveProps) => (
  <div data-slot="order-line-item-remove-frame" className={orderLineItemRemoveFrameClasses}>
    <Button
      size="none"
      data-slot="order-line-item-remove"
      className={clsxm(orderLineItemRemoveClasses, className)}
      {...props}
    />
  </div>
);

/* ── what the item offers alongside itself ────────────────────────────── */

/**
 * The block below a line item: what else is offered with it, and the rail of
 * accessories behind a disclosure.
 */
const orderLineItemExtrasClasses = 'flex flex-col gap-y-6';

export type OrderLineItemExtrasProps = ComponentProps<'div'>;

/** What the item offers alongside itself. */
export const OrderLineItemExtras = ({ className, ...props }: OrderLineItemExtrasProps) => (
  <div
    data-slot="order-line-item-extras"
    className={clsxm(orderLineItemExtrasClasses, className)}
    {...props}
  />
);

/**
 * One offer attached to the line — an extended return window, an insurance, a
 * warranty: a control to accept it beside what it is and what it costs.
 */
const orderLineItemAddOnClasses = 'flex gap-x-4 pt-6';

export type OrderLineItemAddOnProps = ComponentProps<'div'>;

/** An offer attached to the line. */
export const OrderLineItemAddOn = ({ className, ...props }: OrderLineItemAddOnProps) => (
  <div
    data-slot="order-line-item-add-on"
    className={clsxm(orderLineItemAddOnClasses, className)}
    {...props}
  />
);

/** What the offer is, over what it costs. */
const orderLineItemAddOnCopyClasses = 'flex flex-col gap-1';

export type OrderLineItemAddOnCopyProps = ComponentProps<'div'>;

/** The offer's copy column. */
export const OrderLineItemAddOnCopy = ({ className, ...props }: OrderLineItemAddOnCopyProps) => (
  <div
    data-slot="order-line-item-add-on-copy"
    className={clsxm(orderLineItemAddOnCopyClasses, className)}
    {...props}
  />
);

/**
 * The offer's name beside whatever explains it — a mark a shopper can open, a
 * note, nothing at all.
 */
const orderLineItemAddOnLabelClasses = 'flex items-center gap-2';

export type OrderLineItemAddOnLabelProps = ComponentProps<'div'>;

/** The offer's name and what explains it. */
export const OrderLineItemAddOnLabel = ({ className, ...props }: OrderLineItemAddOnLabelProps) => (
  <div
    data-slot="order-line-item-add-on-label"
    className={clsxm(orderLineItemAddOnLabelClasses, className)}
    {...props}
  />
);

/** The offer's name. */
const orderLineItemAddOnTitleClasses = ['font-heading', 'text-foreground text-sm/4 tracking-tight'];

export type OrderLineItemAddOnTitleProps = ComponentProps<'span'>;

/** What the offer is called. */
export const OrderLineItemAddOnTitle = ({ className, ...props }: OrderLineItemAddOnTitleProps) => (
  <span
    data-slot="order-line-item-add-on-title"
    className={clsxm(orderLineItemAddOnTitleClasses, className)}
    {...props}
  />
);

/** What the offer costs. */
const orderLineItemAddOnPriceClasses = 'text-base/6 font-bold';

export type OrderLineItemAddOnPriceProps = ComponentProps<'div'>;

/** The offer's price. */
export const OrderLineItemAddOnPrice = ({ className, ...props }: OrderLineItemAddOnPriceProps) => (
  <div
    data-slot="order-line-item-add-on-price"
    className={clsxm(orderLineItemAddOnPriceClasses, className)}
    {...props}
  />
);
